import React, {useState} from "react";
import {OrderEditorTabProps} from "@widgets/pos/order/OrderEditor";
import {Button, DescriptionList, DescriptionListItem, Icon, InputLabel} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {useController, useFormContext, useWatch} from "react-hook-form";
import {CustomerSearchResult} from "@/data/entities/user";
import {PosOrderForm} from "@/data/entities/order";
import {emptyNewDeliveryAddress, OrderPlacementDeliveryAddress} from "@/data/entities/deliveryAddress";
import {useExistingOrder} from "../../../hooks/UseExistingOrder";
import {MagnifyingGlassIcon} from "@heroicons/react/20/solid";
import CustomerSearchModal from "@widgets/customer/search/CustomerSearchModal";


const CustomerSelector = ({isPending}: OrderEditorTabProps) => {
    const intl = useIntl();
    const [showSearchCustomerModal, setShowSearchCustomerModal] = useState(false);
    const {setValue, getValues} = useFormContext<PosOrderForm>();
    const customer = useWatch<PosOrderForm, "customer">({name: "customer"});
    const {fieldState: {error}} = useController<PosOrderForm, "customerId">({
        name: "customerId",
        rules: {
            validate: (customerId) => getValues("customerType") !== "EXISTING" || customerId !== null
                || intl.formatMessage({id: "selectCustomer"}),
        },
    });
    const isExistingOrder: boolean = useExistingOrder() !== null; // The account of a placed order can't be changed
    // Search, debounced while typing
    // const [query, setQuery] = useState("");
    // const [debouncedQuery, setDebouncedQuery] = useState("");
    // useEffect(() => {
    //     const timeout = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    //     return () => clearTimeout(timeout);
    // }, [query]);

    // const {data: results, isFetching} = useQuery({
    //     queryKey: ["pos-customer-search", debouncedQuery],
    //     queryFn: () => api.user.searchCustomers(debouncedQuery),
    //     enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
    //     staleTime: 30_000,
    // });
    // const options = useMemo(() => (results ?? []).map(toOption), [results]);
    // const selectedOption = useMemo(() => customer === null ? null : toOption(customer), [customer]);

    const selectCustomer = (selected: CustomerSearchResult) => {
        setValue("customerId", selected.id, {shouldValidate: true, shouldDirty: true});
        setValue("customer", selected);
        setValue("contactDetails", {
            firstName: selected.firstName ?? "",
            lastName: selected.lastName ?? "",
            emailAddress: selected.emailAddress ?? null,
            phoneNumber: selected.phoneNumber ?? null,
        }, {shouldDirty: true});
        setValue("deliveryAddress", deliveryAddressFor(selected, getValues("deliveryAddress")), {shouldDirty: true});
    };

    // todo - error when no customer

    return (
        <div className={"w-full flex flex-col gap-4"}>
            {
                !isExistingOrder &&
                <div className={"flex flex-col items-start gap-2"}>
                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                        <FormattedMessage id={"customer"}/>
                    </InputLabel>
                    <Button
                        color={"secondary"}
                        disabled={isPending}
                        iconBefore={<Icon icon={MagnifyingGlassIcon}/>}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    setShowSearchCustomerModal(true);
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={customer === null ? "searchCustomerButton" : "chooseOtherCustomer"}/>
                    </Button>
                    {
                        error?.message &&
                        <p className={"text-sm text-red-600 dark:text-red-400"}>{error.message}</p>
                    }
                    <CustomerSearchModal
                        show={showSearchCustomerModal}
                        setShow={setShowSearchCustomerModal}
                        onSelect={selectCustomer}
                    />
                </div>
                // <Combobox<CustomerSearchResult>
                //     label={
                //         <InputLabel hint={<FormattedMessage id={"required"}/>}>
                //             <FormattedMessage id={"customer"}/>
                //         </InputLabel>
                //     }
                //     tip={
                //         isFetching
                //             ? <FormattedMessage id={"searching"}/>
                //             : <FormattedMessage id={"searchCustomerTip"}/>
                //     }
                //     textFieldProps={{placeholder: intl.formatMessage({id: "searchCustomer"})}}
                //     options={options}
                //     value={selectedOption}
                //     onQueryChange={setQuery}
                //     onChange={(option) => option?.componentProps && selectCustomer(option.componentProps)}
                //     disabled={isPending}
                //     error={error?.message}
                // />
            }

            {
                customer !== null &&
                <DescriptionList compact={true}>
                    <DescriptionListItem
                        compact={true}
                        title={intl.formatMessage({id: "name"})}
                        content={`${customer.firstName} ${customer.lastName}`}
                    />
                    <DescriptionListItem
                        compact={true}
                        title={intl.formatMessage({id: "emailAddress"})}
                        content={customer.emailAddress}
                    />
                    <DescriptionListItem
                        compact={true}
                        title={intl.formatMessage({id: "phoneNumber"})}
                        content={customer.phoneNumber}
                    />
                    <DescriptionListItem
                        compact={true}
                        title={intl.formatMessage({id: "loyaltyPoints"})}
                        content={intl.formatMessage({id: "n-points"}, {n: customer.loyaltyPoints})}
                    />
                </DescriptionList>
            }
        </div>
    );
};

/**
 * Delivery address after selecting a customer: takeaway, a new address that
 * is already being filled in and the business address stay as they are. An
 * address of another account is replaced by the first address of this one.
 */
const deliveryAddressFor = (
    customer: CustomerSearchResult,
    current: OrderPlacementDeliveryAddress,
): OrderPlacementDeliveryAddress => {
    if (current.type === "TAKEAWAY" || current.type === "BUSINESS")
        return current;

    const isOwnAddress = current.type === "EXISTING"
        && customer.deliveryAddresses.some(address => address.id === current.id);
    if (isOwnAddress)
        return current;

    const isNewAddressStarted = current.type === "NEW"
        && (current.address.street ?? "").trim().length > 0;
    if (isNewAddressStarted)
        return current;

    const firstAddress = customer.deliveryAddresses[0];
    return firstAddress ? {type: "EXISTING", id: firstAddress.id} : emptyNewDeliveryAddress();
};

export default CustomerSelector;